import EventEmitter from "eventemitter3";

import {
  SOCKET_EVENTS,
  SOCKET_IDLE_WATCH_INTERVAL,
  SOCKET_RECONNECT_INTERVAL,
  SOCKET_QUEUE_INTERVAL,
  SOCKET_STATE,
} from "./constants";

type SocketEvent = "open" | "close" | "error" | "message";
type SocketCallback = (...args: any[]) => void;

class SocketCore {
  private static __instance: Record<string, SocketCore> = {};
  private url?: string;
  private __callbacks?: {
    open: Array<SocketCallback>;
    close: Array<SocketCallback>;
    error: Array<SocketCallback>;
    message: Array<SocketCallback>;
  };
  private __ws: WebSocket | null = null;
  private __reconnectWS = false;
  private __reconnectInterval?: number;
  private __idleMessageTimer?: number;
  public sendMessageWhenIdle;
  public idleMessagePayload: any;

  constructor(
    url: string,
    sendMessageWhenIdle: boolean,
    idleMessagePayload: any
  ) {
    if (SocketCore.__instance && SocketCore.__instance[url]) {
      return SocketCore.__instance[url];
    }

    this.url = url;
    SocketCore.__instance[url] = this;
    this.__callbacks = { open: [], close: [], error: [], message: [] };
    this.sendMessageWhenIdle = sendMessageWhenIdle;
    this.idleMessagePayload = idleMessagePayload;
    this.__connect();
    this.__sendIdleMessage();
  }

  register(event: SocketEvent, callback: SocketCallback): void {
    if (
      (event === "open" && this.readyState === SOCKET_STATE.OPEN) ||
      (event === "close" && this.readyState === SOCKET_STATE.CLOSED)
    ) {
      callback();
    }
    if (this.__callbacks) {
      this.__callbacks[event].push(callback);
    }
  }

  get readyState(): (typeof SOCKET_STATE)[keyof typeof SOCKET_STATE] {
    if (this.__ws) {
      return this.__ws
        .readyState as (typeof SOCKET_STATE)[keyof typeof SOCKET_STATE];
    }
    return SOCKET_STATE.NULL;
  }

  setBinaryType(value: Blob | ArrayBuffer): void {
    if (!this.__ws) return;

    if (value instanceof Blob) {
      this.__ws.binaryType = "blob";
    } else if (value instanceof ArrayBuffer) {
      this.__ws.binaryType = "arraybuffer";
    }
  }

  send(payload: string | Blob | ArrayBuffer): void {
    if (this.__ws) {
      this.__ws.send(payload);
    }
  }

  __connect(): void {
    if (!this.__ws && this.url) {
      const socket = new WebSocket(this.url);
      socket.onclose = this.__onSocketClose.bind(this);
      socket.onerror = this.__onSocketError.bind(this);
      socket.onmessage = this.__onSocketMessage.bind(this);
      socket.onopen = this.__onSocketOpen.bind(this);
      this.__ws = socket;
      this.__reconnectWS = true;
    }
  }

  close(): void {
    if (this.__ws && this.url) {
      this.__reconnectWS = false;
      this.__ws.close();
      SocketCore.__instance[this.url] = null!;
    }
  }

  __onSocketOpen(_e: Event): void {
    if (this.__callbacks) {
      this.__callbacks.open.forEach((callback) => callback(this));
    }
  }

  __onSocketError(e: Event): void {
    if (this.__callbacks) {
      this.__callbacks.error.forEach((callback) => callback(e));
    }
  }

  __onSocketMessage(e: MessageEvent): void {
    if (this.__callbacks) {
      this.__callbacks.message.forEach((callback) => callback(e));
    }
  }

  __onSocketClose(e: CloseEvent): void {
    const { code } = e;
    if (this.__callbacks)
      this.__callbacks.close.forEach((callback) => callback(e));

    if (code !== 1000 && code !== 1011 && this.__reconnectWS) {
      clearTimeout(this.__reconnectInterval);
      this.__reconnectInterval = window.setTimeout(() => {
        this.__ws = null;
        this.__connect();
      }, SOCKET_RECONNECT_INTERVAL);
    }
  }

  __sendIdleMessage(): void {
    clearTimeout(this.__idleMessageTimer);
    this.__idleMessageTimer = window.setTimeout(() => {
      if (this.sendMessageWhenIdle && this.readyState === SOCKET_STATE.OPEN) {
        this.send(this.idleMessagePayload);
      }
      this.__sendIdleMessage();
    }, SOCKET_IDLE_WATCH_INTERVAL);
  }
}

export class Socket extends EventEmitter {
  private __messageQueue: any[] = [];
  private __ws?: SocketCore;
  private __interval?: number;
  public url: string;
  public sendMessageWhenIdle: boolean;
  public idleMessagePayload: any;
  public ctrl?: any;
  public session?: any;

  constructor(
    url: string,
    sendMessageWhenIdle = false,
    idleMessagePayload: any = ":900",
    ctrl?: any
  ) {
    super();
    this.url = url;
    this.sendMessageWhenIdle = sendMessageWhenIdle;
    this.idleMessagePayload = idleMessagePayload;
    if (ctrl) {
      this.ctrl = ctrl;
      this.session = ctrl.session;
    }
    this.__connect();
  }

  private __connect(): void {
    setTimeout(() => {
      this.__ws = new SocketCore(
        this.url,
        this.sendMessageWhenIdle,
        this.idleMessagePayload
      );
      this.__ws.register("open", this.__onSocketOpen.bind(this));
      this.__ws.register("close", this.__onSocketClose.bind(this));
      this.__ws.register("error", this.__onSocketError.bind(this));
      this.__ws.register("message", this.__onSocketMessage.bind(this));
    }, 50);
  }

  get wsReference(): WebSocket | null {
    return this.__ws?.["__ws"] || null;
  }

  get readyState(): (typeof SOCKET_STATE)[keyof typeof SOCKET_STATE] {
    return this.__ws?.readyState || SOCKET_STATE.NULL;
  }

  addEventListener(event: string, listener: (...args: any[]) => void): this {
    return super.on(event, listener);
  }

  once<T extends string | symbol>(
    event: T,
    listener: (...args: any[]) => void,
    _context?: any
  ): this {
    return super.once(event, listener);
  }

  __onSocketClose(e: CloseEvent): void {
    this.emit(SOCKET_EVENTS.CLOSE, e);
  }

  __onSocketOpen(): void {
    this.emit(SOCKET_EVENTS.OPEN, this);
    this.__startQueueWatch();
  }

  __onSocketError(e: Event): void {
    this.emit(SOCKET_EVENTS.ERROR, e);
  }

  __onSocketMessage(e: MessageEvent): void {
    this.emit(SOCKET_EVENTS.MESSAGE, e.data);
  }

  __clearQueueMessage(): void {
    while (this.__messageQueue.length > 0) {
      if (this.readyState === SOCKET_STATE.OPEN) {
        const message = this.__messageQueue[0];
        try {
          this.__ws?.send(JSON.stringify(message));
          this.__messageQueue.shift();
        } catch (_e) {
          break;
        }
      } else {
        break;
      }
    }
  }

  __startQueueWatch(): void {
    clearTimeout(this.__interval);
    this.__interval = window.setTimeout(() => {
      if (this.__ws && this.readyState === SOCKET_STATE.OPEN) {
        this.__clearQueueMessage();
      }
      this.__startQueueWatch();
    }, SOCKET_QUEUE_INTERVAL);
  }

  disconnect(): void {
    this.__ws?.close();
  }

  addInQueue(payload: any): void {
    this.__messageQueue.push(payload);
  }

  sendJson(payload: any): void {
    this.send(JSON.stringify(payload));
  }

  sendBinary(payload: Blob | ArrayBuffer): void {
    if (this.__ws) {
      this.__ws.setBinaryType(payload);
      this.send(payload);
    }
  }

  send(payload: string | Blob | ArrayBuffer): void {
    if (!this.__ws) return;

    if (this.readyState === SOCKET_STATE.OPEN) {
      try {
        this.__ws.send(payload);
      } catch (_e) {
        this.addInQueue(payload);
      }
    } else {
      this.addInQueue(payload);
    }
  }
}
